import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FileText, ArrowLeft, Save, Eye, Building2, Calendar, User, 
  FileCheck, Link2, Loader2, CheckCircle, AlertCircle 
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  getPolicyTemplate, 
  createGeneratedPolicy, 
  previewPolicyTemplate,
  getFrameworkBadgeColor
} from '../../services/policyTemplates';

export default function PolicyCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('template');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [template, setTemplate] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    organization_name: '',
    owner: '',
    effective_date: '',
    review_date: '',
    framework_id: '',
    control_ids: [],
    placeholder_values: {}
  });

  useEffect(() => {
    if (templateId) {
      loadTemplate();
    } else {
      setLoading(false);
    }
  }, [templateId]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const data = await getPolicyTemplate(templateId);
      setTemplate(data);
      
      // Pre-fill title from template
      setFormData(prev => ({
        ...prev,
        title: data.name,
        placeholder_values: {}
      }));
      
      // Initialize placeholder values
      if (data.placeholders) {
        const initialPlaceholders = {};
        data.placeholders.forEach(ph => {
          initialPlaceholders[ph.key] = ph.default_value || '';
        });
        setFormData(prev => ({
          ...prev,
          placeholder_values: initialPlaceholders
        }));
      }
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!formData.organization_name) {
      toast.error('Please enter your organization name to preview');
      return;
    }
    
    try {
      setPreviewing(true);
      const preview = await previewPolicyTemplate(templateId, formData.organization_name);
      setPreviewContent(preview.preview_content);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!template) {
      toast.error('Please select a template first');
      return;
    }
    
    if (!formData.title || !formData.organization_name) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      setSaving(true);
      
      const policyData = {
        template_id: templateId,
        title: formData.title,
        organization_name: formData.organization_name,
        owner: formData.owner || '',
        effective_date: formData.effective_date || null,
        review_date: formData.review_date || null,
        framework_id: formData.framework_id || null,
        control_ids: formData.control_ids,
        placeholder_values: formData.placeholder_values
      };
      
      const result = await createGeneratedPolicy(policyData);
      toast.success('Policy created successfully!');
      navigate(`/grc/policy-templates/view/${result.id}`);
    } catch (error) {
      console.error('Error creating policy:', error);
      toast.error(error.response?.data?.detail || 'Failed to create policy');
    } finally {
      setSaving(false);
    }
  };

  const handlePlaceholderChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      placeholder_values: {
        ...prev.placeholder_values,
        [key]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!templateId) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/grc/policy-templates')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Templates
        </Button>
        
        <div className="text-center py-12 bg-white rounded-lg border">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Template Selected</h2>
          <p className="text-gray-500 mb-6">Please select a template to create a policy</p>
          <Button onClick={() => navigate('/grc/policy-templates')}>
            Browse Templates
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="policy-create-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button variant="ghost" onClick={() => navigate('/grc/policy-templates')} className="mb-2">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Templates
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-blue-600" />
            Create Policy from Template
          </h1>
          {template && (
            <p className="text-gray-500 mt-1">Using template: {template.name}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Section */}
        <div className="bg-white rounded-lg border shadow-sm p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Template Info */}
            {template && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                  <FileCheck className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-blue-900">{template.name}</h3>
                    <p className="text-sm text-blue-700 mt-1">{template.description}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {template.frameworks?.map(fw => (
                        <Badge 
                          key={fw} 
                          variant="secondary"
                          className={`bg-${getFrameworkBadgeColor(fw)}-100 text-${getFrameworkBadgeColor(fw)}-700`}
                        >
                          {fw}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Policy Title */}
            <div className="space-y-2">
              <Label htmlFor="title" className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                Policy Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter policy title"
                data-testid="policy-title-input"
              />
            </div>

            {/* Organization Name */}
            <div className="space-y-2">
              <Label htmlFor="organization_name" className="flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                Organization Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="organization_name"
                value={formData.organization_name}
                onChange={(e) => setFormData(prev => ({ ...prev, organization_name: e.target.value }))}
                placeholder="Your organization name"
                data-testid="org-name-input"
              />
            </div>

            {/* Owner */}
            <div className="space-y-2">
              <Label htmlFor="owner" className="flex items-center gap-1">
                <User className="w-4 h-4" />
                Policy Owner
              </Label>
              <Input
                id="owner"
                value={formData.owner}
                onChange={(e) => setFormData(prev => ({ ...prev, owner: e.target.value }))}
                placeholder="Policy owner name or role"
                data-testid="owner-input"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="effective_date" className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Effective Date
                </Label>
                <Input
                  id="effective_date"
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
                  data-testid="effective-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="review_date" className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Review Date
                </Label>
                <Input
                  id="review_date"
                  type="date"
                  value={formData.review_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, review_date: e.target.value }))}
                  data-testid="review-date-input"
                />
              </div>
            </div>

            {/* Custom Placeholders */}
            {template?.placeholders && template.placeholders.length > 0 && (
              <div className="space-y-4">
                <Label className="flex items-center gap-1">
                  <Link2 className="w-4 h-4" />
                  Template Variables
                </Label>
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                  {template.placeholders.map(ph => (
                    <div key={ph.key} className="space-y-1">
                      <Label htmlFor={ph.key} className="text-sm text-gray-600">
                        {ph.label || ph.key.replace(/[{}]/g, '')}
                        {ph.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <Input
                        id={ph.key}
                        value={formData.placeholder_values[ph.key] || ''}
                        onChange={(e) => handlePlaceholderChange(ph.key, e.target.value)}
                        placeholder={ph.description || `Enter ${ph.label}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handlePreview}
                disabled={previewing || !formData.organization_name}
                data-testid="preview-btn"
              >
                {previewing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Preview
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="flex-1"
                data-testid="create-policy-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Create Policy
              </Button>
            </div>
          </form>
        </div>

        {/* Preview Section */}
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Policy Preview
            </h3>
          </div>
          <ScrollArea className="h-[600px]">
            <div className="p-6">
              {previewContent ? (
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: previewContent }}
                />
              ) : template ? (
                <div className="space-y-4">
                  <div className="text-center py-8 text-gray-500">
                    <Eye className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Enter your organization name and click "Preview" to see the generated policy</p>
                  </div>
                  
                  {/* Template Structure Preview */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-700 mb-3">Template Structure:</h4>
                    <div className="space-y-2">
                      {template.sections?.map((section, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded border-l-4 border-blue-400">
                          <p className="font-medium text-gray-800">{idx + 1}. {section.title}</p>
                          {section.subsections && (
                            <div className="mt-2 pl-4 space-y-1">
                              {section.subsections.slice(0, 3).map((sub, subIdx) => (
                                <p key={subIdx} className="text-sm text-gray-600">
                                  {idx + 1}.{subIdx + 1} {sub.title}
                                </p>
                              ))}
                              {section.subsections.length > 3 && (
                                <p className="text-sm text-gray-400">
                                  +{section.subsections.length - 3} more...
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a template to see preview</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
